"use client";

import { type ReactNode, type RefObject, useId, useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { downloadPng } from "@/lib/tool-kit/download";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/**
 * The frame and drawing vocabulary for the geometry family's labelled figures.
 *
 * Colours and fonts are attributes, never classes, so the SVG downloads as a PNG that
 * looks like the one on the page. All visible text arrives through props: a figure's
 * labels are measurements the reader typed plus single-letter symbols.
 */

export const DIAGRAM_WIDTH = 360;
export const DIAGRAM_HEIGHT = 240;
const FONT = "system-ui, -apple-system, Segoe UI, sans-serif";
const UNSAFE_ID = /[^a-zA-Z0-9_-]/g;

export const SHAPE = {
  stroke: CHART_COLORS[0],
  fill: CHART_COLORS[0],
  fillOpacity: 0.14,
  guide: CHART_COLORS[3],
  accent: CHART_COLORS[1],
  ink: CHART_INK.text,
  muted: CHART_INK.muted,
} as const;

export function DiagramFrame({
  title,
  svgRef,
  children,
  height = DIAGRAM_HEIGHT,
}: {
  /** The figure's accessible name: what it shows, in a sentence. */
  title: string;
  svgRef?: RefObject<SVGSVGElement | null>;
  children: ReactNode;
  height?: number;
}) {
  const titleId = `geo-title-${useId().replace(UNSAFE_ID, "")}`;
  return (
    <svg
      aria-labelledby={titleId}
      className="mx-auto block h-auto w-full max-w-md rounded-lg border border-border bg-white"
      fontFamily={FONT}
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${DIAGRAM_WIDTH} ${height}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title id={titleId}>{title}</title>
      <rect fill="#ffffff" height={height} width={DIAGRAM_WIDTH} />
      {children}
    </svg>
  );
}

/** A label: measurement text in the figure's ink. */
export function Label({
  x,
  y,
  children,
  anchor = "middle",
  color = SHAPE.ink,
  size = 13,
  italic = false,
}: {
  x: number;
  y: number;
  children: ReactNode;
  anchor?: "start" | "middle" | "end";
  color?: string;
  size?: number;
  italic?: boolean;
}) {
  return (
    <text
      fill={color}
      fontSize={size}
      fontStyle={italic ? "italic" : undefined}
      textAnchor={anchor}
      x={x}
      y={y}
    >
      {children}
    </text>
  );
}

/** A dashed guide line: a height, a radius, a diagonal. */
export function Guide({
  x1,
  y1,
  x2,
  y2,
  color = SHAPE.guide,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color?: string;
}) {
  return (
    <line
      stroke={color}
      strokeDasharray="5 4"
      strokeWidth={2}
      x1={x1}
      x2={x2}
      y1={y1}
      y2={y2}
    />
  );
}

/** The small square that marks a right angle at (x, y); dx and dy point into the angle. */
export function RightAngle({
  x,
  y,
  dx,
  dy,
  size = 9,
}: {
  x: number;
  y: number;
  dx: 1 | -1;
  dy: 1 | -1;
  size?: number;
}) {
  return (
    <path
      d={`M${x + dx * size} ${y} L${x + dx * size} ${y + dy * size} L${x} ${y + dy * size}`}
      fill="none"
      stroke={SHAPE.guide}
      strokeWidth={1.5}
    />
  );
}

/** One legend row: a sample of the mark it names at (x, y), then its text. */
export function LegendRow({
  x,
  y,
  text,
  sample,
}: {
  x: number;
  y: number;
  text: string;
  sample: ReactNode;
}) {
  return (
    <>
      <g transform={`translate(${x} ${y})`}>{sample}</g>
      <Label anchor="start" x={x + 26} y={y + 4}>
        {text}
      </Label>
    </>
  );
}

/** A filled outline in the figure's colour. */
export function Outline({ d }: { d: string }) {
  return (
    <path
      d={d}
      fill={SHAPE.fill}
      fillOpacity={SHAPE.fillOpacity}
      stroke={SHAPE.stroke}
      strokeLinejoin="round"
      strokeWidth={2.5}
    />
  );
}

export function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** The largest w × h of the given aspect ratio (w/h) that fits a box, ratio kept readable. */
export function fitBox(
  aspect: number,
  maxWidth: number,
  maxHeight: number
): { w: number; h: number } {
  const ratio = clamp(Number.isFinite(aspect) ? aspect : 1, 0.25, 4);
  let w = maxWidth;
  let h = w / ratio;
  if (h > maxHeight) {
    h = maxHeight;
    w = h * ratio;
  }
  return { w, h };
}

/** "Download PNG" for a figure. */
export function DiagramDownload({
  svgRef,
  fileName,
  label,
}: {
  svgRef: RefObject<SVGSVGElement | null>;
  fileName: string;
  label: string;
}) {
  return (
    <button
      className={BUTTON_SMALL}
      onClick={() => {
        if (svgRef.current) {
          downloadPng(`${fileName}.png`, svgRef.current, { scale: 3 });
        }
      }}
      type="button"
    >
      {label}
    </button>
  );
}

/** A ref for a figure and its download button. */
export function useDiagramRef(): RefObject<SVGSVGElement | null> {
  return useRef<SVGSVGElement>(null);
}
